import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import fetchPrebuiltTemplates, { FetchPrebuiltTemplatesParams } from './prebuiltTemplatesApi';
import { PrebuiltTemplate, UserDefinedTemplate } from './types';
import fetchUserDefinedTemplates from './userDefinedTemplatesApi';

export type TemplateStatus =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'UNDER_REVIEW'
  | 'ARCHIVED'
  | 'APPROVED'
  | 'REJECTED'
  | string;

export interface BaseTemplate {
  id: string;
  name: string;
  html: string;
  css: string;
  createdAt: string | number;
  thumbnail?: string | null;
  isPrebuilt?: boolean;
  isUserDefined?: boolean;
  category: string;
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
}

export interface CategoryCounts {
  [category: string]: number;
}

interface TemplatesState {
  userTemplates: BaseTemplate[];
  userDefinedTemplates: UserDefinedTemplate[];
  prebuiltTemplates: PrebuiltTemplate[];
  prebuiltStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  userDefinedStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  prebuiltError?: string;
  userDefinedError?: string;
  currentTemplateId: string | null;
  userDefinedPagination: PaginationState;
  prebuiltPagination: PaginationState; // Add this
  userDefinedSearchTerm: string;
  prebuiltSearchTerm: string;
  userDefinedCategoryCounts: CategoryCounts;
  prebuiltCategoryCounts: CategoryCounts;
}

const initialState: TemplatesState = {
  userTemplates: JSON.parse(localStorage.getItem('templates') || '[]'),
  userDefinedTemplates: [],
  prebuiltTemplates: [],
  prebuiltStatus: 'idle',
  userDefinedStatus: 'idle',
  currentTemplateId: localStorage.getItem('currentTemplateId') || null,
  userDefinedPagination: {
    currentPage: 0,
    totalPages: 0,
    totalCount: 0,
    pageSize: 9,
  },
  prebuiltPagination: { // Add this
    currentPage: 0,
    totalPages: 0,
    totalCount: 0,
    pageSize: 9,
  },
  userDefinedSearchTerm: '',
  prebuiltSearchTerm: '',
  userDefinedCategoryCounts: {},
  prebuiltCategoryCounts: {},
};

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    addTemplate: {
      reducer(state, action: PayloadAction<BaseTemplate>) {
        state.userTemplates.push(action.payload);
        localStorage.setItem('templates', JSON.stringify(state.userTemplates));
      },
      prepare({
        name,
        html,
        css,
        thumbnail,
        category = 'Default',
      }: {
        name: string;
        html: string;
        css: string;
        thumbnail?: string | null;
        category?: string;
      }) {
        return {
          payload: {
            id: crypto.randomUUID(),
            name,
            html,
            css,
            createdAt: new Date().toISOString(),
            thumbnail,
            category,
            isPrebuilt: false,
          } as BaseTemplate,
        };
      },
    },

    updateTemplate(
      state,
      action: PayloadAction<Partial<BaseTemplate> & { id: string }>
    ) {
      const { id, ...updates } = action.payload;
      const index = state.userTemplates.findIndex((t) => t.id === id);
      if (index !== -1) {
        state.userTemplates[index] = {
          ...state.userTemplates[index],
          ...updates,
        };
        localStorage.setItem('templates', JSON.stringify(state.userTemplates));
      }
    },

    removeTemplate(state, action: PayloadAction<string>) {
      state.userTemplates = state.userTemplates.filter(
        (t) => t.id !== action.payload
      );
      localStorage.setItem('templates', JSON.stringify(state.userTemplates));
    },

    updateUserDefinedTemplateMetadata(
      state,
      action: PayloadAction<{
        id: string;
        name?: string;
        description?: string;
        status?: TemplateStatus;
        updatedAt?: string;
      }>
    ) {
      const { id, name, description, status, updatedAt } = action.payload;
      const index = state.userDefinedTemplates.findIndex((t) => t.id === id);

      if (index !== -1) {
        const template = state.userDefinedTemplates[index];
        const updates: Partial<UserDefinedTemplate> = {};

        if (name !== undefined) updates.name = name;
        if (description !== undefined) updates.description = description;

        if (status !== undefined) {
          updates.status = status as any;
        }

        updates.updatedAt = updatedAt || new Date().toISOString();

        state.userDefinedTemplates[index] = {
          ...template,
          ...updates,
        };
      }
    },

    replaceUserDefinedTemplate(
      state,
      action: PayloadAction<UserDefinedTemplate>
    ) {
      const updatedTemplate = action.payload;
      const index = state.userDefinedTemplates.findIndex(
        (t) => t.id === updatedTemplate.id
      );

      if (index !== -1) {
        state.userDefinedTemplates[index] = updatedTemplate;
      } else {
        state.userDefinedTemplates.push(updatedTemplate);
      }
    },

    updateUserDefinedTemplate(
      state,
      action: PayloadAction<Partial<UserDefinedTemplate> & { id: string }>
    ) {
      const { id, ...updates } = action.payload;
      const index = state.userDefinedTemplates.findIndex((t) => t.id === id);

      if (index !== -1) {
        const finalUpdates = {
          ...updates,
          updatedAt: updates.updatedAt || new Date().toISOString(),
        };

        state.userDefinedTemplates[index] = {
          ...state.userDefinedTemplates[index],
          ...finalUpdates,
        };
      }
    },

    removeUserDefinedTemplate(state, action: PayloadAction<string>) {
      state.userDefinedTemplates = state.userDefinedTemplates.filter(
        (t) => t.id !== action.payload
      );
    },

    setUserDefinedTemplates(
      state,
      action: PayloadAction<UserDefinedTemplate[]>
    ) {
      state.userDefinedTemplates = action.payload;
    },

    setCurrentTemplateId(state, action: PayloadAction<string>) {
      state.currentTemplateId = action.payload;
      localStorage.setItem('currentTemplateId', action.payload);
    },

    clearCurrentTemplateId(state) {
      state.currentTemplateId = null;
      localStorage.removeItem('currentTemplateId');
    },

    setUserDefinedPagination(state, action: PayloadAction<Partial<PaginationState>>) {
      state.userDefinedPagination = {
        ...state.userDefinedPagination,
        ...action.payload,
      };
    },

    setPrebuiltPagination(state, action: PayloadAction<Partial<PaginationState>>) {
      state.prebuiltPagination = {
        ...state.prebuiltPagination,
        ...action.payload,
      };
    },

    setUserDefinedSearchTerm(state, action: PayloadAction<string>) {
      state.userDefinedSearchTerm = action.payload;
    },

    setPrebuiltSearchTerm(state, action: PayloadAction<string>) {
      state.prebuiltSearchTerm = action.payload;
    },

    setUserDefinedCategoryCounts(state, action: PayloadAction<CategoryCounts>) {
      state.userDefinedCategoryCounts = action.payload;
    },

    setPrebuiltCategoryCounts(state, action: PayloadAction<CategoryCounts>) {
      state.prebuiltCategoryCounts = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPrebuiltTemplates.pending, (state) => {
        state.prebuiltStatus = 'loading';
      })
      .addCase(fetchPrebuiltTemplates.fulfilled, (state, action) => {
        state.prebuiltStatus = 'succeeded';
        state.prebuiltTemplates = action.payload.content;
        state.prebuiltPagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          totalCount: action.payload.totalCount,
          pageSize: action.payload.pageSize,
        };
        const meta = action.meta as { arg: FetchPrebuiltTemplatesParams };
        state.prebuiltSearchTerm = meta.arg.name || '';
      })
      .addCase(fetchPrebuiltTemplates.rejected, (state, action) => {
        state.prebuiltStatus = 'failed';
        state.prebuiltError = action.payload || 'Unknown error';
      })
      .addCase(fetchUserDefinedTemplates.pending, (state) => {
        state.userDefinedStatus = 'loading';
      })
      .addCase(fetchUserDefinedTemplates.fulfilled, (state, action) => {
        state.userDefinedStatus = 'succeeded';
        state.userDefinedTemplates = action.payload.content;
        state.userDefinedPagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          totalCount: action.payload.totalCount,
          pageSize: action.payload.pageSize,
        };
        const meta = action.meta as { arg: { name?: string } };
        state.userDefinedSearchTerm = meta.arg.name || '';
      })
      .addCase(fetchUserDefinedTemplates.rejected, (state, action) => {
        state.userDefinedStatus = 'failed';
        state.userDefinedError = action.payload || 'Unknown error';
      });
  },
});

export const {
  addTemplate,
  updateTemplate,
  removeTemplate,
  updateUserDefinedTemplateMetadata,
  replaceUserDefinedTemplate,
  updateUserDefinedTemplate,
  removeUserDefinedTemplate,
  setUserDefinedTemplates,
  setCurrentTemplateId,
  clearCurrentTemplateId,
  setUserDefinedPagination,
  setPrebuiltPagination,
  setUserDefinedSearchTerm,
  setPrebuiltSearchTerm,
  setUserDefinedCategoryCounts,
  setPrebuiltCategoryCounts,
} = templatesSlice.actions;

export default templatesSlice.reducer;